import { useEffect, useState } from 'react';
import type { Operaciones } from '../../../api/operaciones';
import type { Pago } from '../../../api/ops/pagos';
import type { PaseDelNegocio } from '../../../api/ops/pases';
import type { Reserva } from '../../../api/ops/reservas';
import { claveIdempotencia } from '../../../api/comun';
import { ErrorApi } from '../../../api/problema';
import { useAvisoBreve } from '../../../componentes/avisoBreve';
import { Campo } from '../../../componentes/Campo';
import { Chip, type EstadoChip } from '../../../componentes/Chip';
import { Icono } from '../../../componentes/Icono';
import { Ventana } from '../../../componentes/Ventana';
import { dinero } from '../../../logica/dinero';
import { avisoBreveDeError } from '../../../logica/estados';
import { usePermisos } from '../../../permisos/permisos';
import { CONCEPTO, ESTADO_PAGO } from '../../pagos/textos';
import type { ContextoReservas } from '../contexto';
import { diaLargo, horaEn } from '../fechas';
import { mayuscula } from '../textos';

/**
 * Pestañas «Pagos» y «Pase» de 8.3 (`diseno/reservas-sala`, capturas `8.3--normal-pagos` y `8.3--normal-pase`).
 *
 * - Pagos: la política que se congeló en la reserva (`getPaymentPolicyVersion`, su número y su `conditions_text`, que es
 *   el único texto de cobro; el nombre, el `policy_name` de la zona) y los pagos de la reserva (`listPayments` con
 *   `payable_type=reserva`), cada uno con «Ver en Pagos» (`/pagos?pago=`). Sin `pagos.pagos.ver`, solo la política.
 * - Pase: el pase de la reserva (`listPasses`, el más reciente; uno nuevo al cambiar fecha, hora o personas, G466) con su
 *   código, cuándo vale y su estado; la llegada; «Ha llegado» (`validatePass`, `manual`, D-047), «Deshacer la llegada»
 *   (`undoArrival` con motivo) y «Reenviar el pase» (`resendPass`, `409` si está anulado). Sin pase y pendiente de pago,
 *   lo dice: el pase lo emite Pases al confirmar (G411).
 */
const ESTADO_PASE: Readonly<Record<PaseDelNegocio['status'], readonly [string, EstadoChip]>> = {
    active: ['Válido', 'confirmed'],
    inside: ['Dentro', 'live'],
    voided: ['Anulado', 'problem'],
    expired: ['Caducado', 'neutral'],
};

const PERMISOS = { pagos: 'pagos.pagos.ver', pases: 'pases.pases.ver', validar: 'pases.puerta.validar', deshacer: 'pases.llegadas.deshacer', reenviar: 'pases.pases.reenviar' } as const;

interface Props {
    ops: Operaciones;
    negocio: string;
    reserva: Reserva;
    contexto: ContextoReservas;
    soloLectura: boolean;
    navegar?: ((ruta: string) => void) | undefined;
    onCambio: () => void;
}

const cuando = (iso: string, zona: string) => `${mayuscula(diaLargo(iso.slice(0, 10)))}, ${horaEn(iso, zona)}`;

export function PestanaPagos({ ops, negocio, reserva, contexto, navegar }: Props) {
    const permisos = usePermisos();
    const vePagos = permisos.puede(PERMISOS.pagos);
    const [pagos, setPagos] = useState<{ id: string; lista: Pago[] | null } | null>(null);
    const [politica, setPolitica] = useState<{ numero: number; texto: string } | null>(null);
    const versionId = reserva.policy_version_id ?? null;

    useEffect(() => {
        if (!vePagos) {
            return;
        }
        let vivo = true;
        ops.listPayments(negocio, { payable_type: 'reserva', payable_id: reserva.id, limit: 50 })
            .then((l) => { if (vivo) { setPagos({ id: reserva.id, lista: l.data }); } })
            .catch(() => { if (vivo) { setPagos({ id: reserva.id, lista: null }); } });
        return () => { vivo = false; };
    }, [ops, negocio, reserva.id, vePagos]);

    useEffect(() => {
        if (versionId === null) {
            return;
        }
        let vivo = true;
        ops.getPaymentPolicyVersion(negocio, versionId)
            .then((v) => { if (vivo) { setPolitica({ numero: v.number, texto: v.conditions_text }); } })
            .catch(() => undefined);
        return () => { vivo = false; };
    }, [ops, negocio, versionId]);

    const nombrePolitica = contexto.zonas.find((z) => z.id === reserva.zone_id)?.policy_name ?? null;
    const lista = pagos?.id === reserva.id ? pagos.lista : undefined;
    return (
        <>
            {versionId === null ? (
                <p className="rv-sl-nota">Esta reserva no tiene política de cobro: no se pidió nada al reservar.</p>
            ) : (
                <dl className="rv-rs-presupuesto">
                    <div><dt>Política</dt><dd>{[nombrePolitica, politica === null ? null : `versión ${String(politica.numero)}`].filter((x) => x !== null).join(' • ') || '—'}</dd></div>
                    {politica !== null && <div><dt>Qué pide</dt><dd className="rv-rs-presupuesto__texto">{politica.texto}</dd></div>}
                </dl>
            )}
            {vePagos && (
                <section aria-label="Pagos de esta reserva">
                    <h3 className="rv-rs-subtitulo">Pagos de esta reserva</h3>
                    {lista === undefined ? (
                        <p className="rv-sl-nota">Cargando…</p>
                    ) : lista === null ? (
                        <p className="rv-sl-nota">No se han podido cargar los pagos.</p>
                    ) : lista.length === 0 ? (
                        <p className="rv-sl-nota">Esta reserva no tiene pagos.</p>
                    ) : (
                        <ul className="rv-sl-desplazadas">
                            {lista.map((p) => {
                                const [texto, estado] = ESTADO_PAGO[p.status];
                                return (
                                    <li key={p.id} className="rv-rs-pago">
                                        <span>
                                            <strong>{CONCEPTO[p.concept]}</strong>
                                            <small>{cuando(p.created_at, contexto.zona)}</small>
                                        </span>
                                        <span className="rv-rs-pago__der">
                                            <strong>{p.concept === 'guarantee' ? `hasta ${dinero(p.amount)}` : dinero(p.amount)}</strong>
                                            <Chip estado={estado}>{texto}</Chip>
                                            {navegar !== undefined && (
                                                <button type="button" className="rv-b rv-b-sec" onClick={() => { navegar(`/pagos?pago=${encodeURIComponent(p.id)}`); }}>
                                                    <Icono nombre="arrow-right" />
                                                    <span>Ver en Pagos</span>
                                                </button>
                                            )}
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </section>
            )}
        </>
    );
}

export function PestanaPase({ ops, negocio, reserva, contexto, soloLectura, onCambio }: Props) {
    const permisos = usePermisos();
    const avisar = useAvisoBreve();
    const [pase, setPase] = useState<{ id: string; pase: PaseDelNegocio | null; error: boolean } | null>(null);
    const [intento, setIntento] = useState(0);
    const [ocupado, setOcupado] = useState(false);
    const [deshacer, setDeshacer] = useState<string | null>(null);
    const vePases = permisos.puede(PERMISOS.pases);

    useEffect(() => {
        if (!vePases) {
            return;
        }
        let vivo = true;
        ops.listPasses(negocio, { passable_type: 'reserva', passable_id: reserva.id, limit: 10 })
            .then((l) => {
                if (!vivo) {
                    return;
                }
                const orden = [...l.data].sort((a, b) => b.issued_at.localeCompare(a.issued_at));
                const vivoPase = orden.find((p) => p.status === 'active' || p.status === 'inside') ?? orden[0] ?? null;
                setPase({ id: reserva.id, pase: vivoPase, error: false });
            })
            .catch(() => { if (vivo) { setPase({ id: reserva.id, pase: null, error: true }); } });
        return () => { vivo = false; };
    }, [ops, negocio, reserva.id, vePases, intento]);

    if (!vePases) {
        return <p className="rv-sl-nota">No puedes ver los pases.</p>;
    }
    const actual = pase?.id === reserva.id ? pase : null;
    if (actual === null) {
        return <p className="rv-sl-nota">Cargando…</p>;
    }
    if (actual.error) {
        return <p className="rv-sl-nota">No se ha podido cargar el pase.</p>;
    }
    const p = actual.pase;
    const fallo = (e: unknown, defecto: string) => { avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? (e instanceof ErrorApi ? e.message : defecto) }); };
    const recargar = () => { setIntento((i) => i + 1); onCambio(); };

    if (p === null) {
        return (
            <p className="rv-sl-nota">
                {reserva.status === 'pending_payment'
                    ? 'Sin pase: falta el pago. Cuando pague, le enviamos el pase.'
                    : 'Esta reserva no tiene pase.'}
            </p>
        );
    }
    const [texto, estado] = ESTADO_PASE[p.status];
    const hasta = horaEn(p.valid_to, contexto.zona);
    const llegar = () => {
        setOcupado(true);
        ops.validatePass(negocio, { method: 'manual', pass_id: p.id, client_scan_id: claveIdempotencia() })
            .then((r) => {
                avisar(r.result === 'green' ? { tipo: 'ok', texto: 'Llegada registrada.' } : { tipo: 'error', texto: r.message ?? 'No se ha podido registrar la llegada.' });
                recargar();
            })
            .catch((e: unknown) => { fallo(e, 'No se ha podido registrar la llegada.'); })
            .finally(() => { setOcupado(false); });
    };
    const reenviar = () => {
        setOcupado(true);
        ops.resendPass(negocio, p.id)
            .then(() => { avisar({ tipo: 'ok', texto: 'Pase reenviado.' }); })
            .catch((e: unknown) => {
                if (e instanceof ErrorApi && e.estado === 409) {
                    // G1080: «sin contacto» (`no_contact`) no es lo mismo que un pase anulado o caducado (`conflict`).
                    avisar({ tipo: 'error', texto: e.codigo === 'no_contact' ? 'No se puede reenviar el pase: el cliente no tiene un contacto al que mandárselo.' : 'No se puede reenviar el pase: ya no vale.' });
                } else {
                    fallo(e, 'No se ha podido reenviar el pase.');
                }
            })
            .finally(() => { setOcupado(false); });
    };

    return (
        <>
            <div className="rv-rs-pase">
                <span className="rv-rs-pase__qr" aria-hidden="true"><Icono nombre="qr-code" /></span>
                <span>
                    <strong>{p.code}</strong>
                    <small>{`Válido de ${horaEn(p.valid_from, contexto.zona)} a ${hasta} • ${mayuscula(diaLargo(p.valid_from.slice(0, 10)))}`}</small>
                </span>
                <Chip estado={estado}>{texto}</Chip>
            </div>
            <dl className="rv-rs-presupuesto">
                <div><dt>Emitido</dt><dd>{cuando(p.issued_at, contexto.zona)}</dd></div>
                <div><dt>Llegada</dt><dd>{reserva.arrived_at == null ? 'Sin llegada' : cuando(reserva.arrived_at, contexto.zona)}</dd></div>
            </dl>
            {!soloLectura && (
                <div className="rv-rs-acciones">
                    {p.status === 'active' && permisos.puede(PERMISOS.validar) && (
                        <button type="button" className="rv-b rv-b-sec" disabled={ocupado} onClick={llegar}><Icono nombre="log-in" /><span>Ha llegado</span></button>
                    )}
                    {p.status === 'inside' && permisos.puede(PERMISOS.deshacer) && (
                        <button type="button" className="rv-b rv-b-sec" disabled={ocupado} onClick={() => { setDeshacer(''); }}><Icono nombre="undo-2" /><span>Deshacer la llegada</span></button>
                    )}
                    {(p.status === 'active' || p.status === 'inside') && permisos.puede(PERMISOS.reenviar) && (
                        <button type="button" className="rv-b rv-b-sec" disabled={ocupado} onClick={reenviar}><Icono nombre="send" /><span>Reenviar el pase</span></button>
                    )}
                </div>
            )}
            <Ventana
                abierta={deshacer !== null}
                titulo="¿Deshacer la llegada?"
                texto="La reserva vuelve a estar por llegar y el pase vuelve a valer."
                accion="Deshacer la llegada"
                accionIcono="undo-2"
                cancelar="Volver"
                ocupado={ocupado}
                inactiva={(deshacer ?? '').trim() === ''}
                onAccion={() => {
                    setOcupado(true);
                    ops.undoArrival(negocio, p.id, (deshacer ?? '').trim())
                        .then(() => { avisar({ tipo: 'ok', texto: 'Llegada deshecha.' }); setDeshacer(null); recargar(); })
                        .catch((e: unknown) => { fallo(e, 'No se ha podido deshacer la llegada.'); })
                        .finally(() => { setOcupado(false); });
                }}
                onCerrar={() => { setDeshacer(null); }}
            >
                <Campo etiqueta="Motivo" valor={deshacer ?? ''} onChange={setDeshacer} max={500} />
            </Ventana>
        </>
    );
}
